import {booleanAttribute, Component, effect, inject, input, output, signal} from '@angular/core';
import {Field, form, FormField, validateStandardSchema} from '@angular/forms/signals';
import {z} from 'zod';
import {AppBaseComponent} from '../../../shared/components/app-base-component/app-base-component';
import {ZodAutoValidateDirective} from '../../../shared/decorators/auto-validate/auto-validate';
import {SelectButton} from 'primeng/selectbutton';
import {InputText} from 'primeng/inputtext';
import {Select} from 'primeng/select';
import {AttributeService} from '../../../app-code/services/attribute-service';
import {JsonPipe} from '@angular/common';
import {SelectField} from '../../../shared/components/select-field/select-field';
import {Slider} from 'primeng/slider';
import {CmToFeetPipe} from '../../../shared/pipes/cm-to-feet-pipe';
import {finalize} from 'rxjs';

const validationScheme = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  sex: z.string().min(1),
  birth_year: z.string().min(1, 'Birth year is required'),
  body_build: z.string().min(1),
  complexion: z.string().min(1),
  height: z.number(),
  weight: z.string().min(1),
  profile_for: z.string().min(1),
}).refine((data) => {
  console.log('Refine validation called with data:', data);
  if (!data.birth_year || data.birth_year === '') {
    return true; // Let the required field validation handle empty case
  }
  const currentYear = new Date().getFullYear();
  const birthYear = parseInt(data.birth_year);
  const age = currentYear - birthYear;
  console.log('user age is ' + age, 'birth_year:', data.birth_year);
  const isValid = age >= 18 && age <= 70;
  console.log('Age validation result:', isValid);
  return isValid;
}, {
  message: "Age must be between 18 and 70 years",
  path: ["birth_year"]
});


@Component({
  selector: 'app-personal-info',
  imports: [
    ZodAutoValidateDirective,
    SelectButton,
    SelectField,
    InputText,
    Slider,
    CmToFeetPipe,
    FormField,
  ],
  templateUrl: './personal-info.html',
  styleUrl: './personal-info.css',
})
export class PersonalInfo extends AppBaseComponent {
  weightOptions = signal<Array<{ label: string; value: string }>>(this.generateRangeOptions());
  standalone = input(false, {transform: booleanAttribute});
  attributeService = inject(AttributeService);
  saved = output();
  formModel = signal({
    profile_for: '',
    first_name: '',
    last_name: '',
    sex: '',
    birth_year: '',
    height: NaN,
    weight: '',
    body_build: '',
    complexion: '',
  });
  form = form(this.formModel, (schemaPath) => {
    console.log('Form validation triggered for path:', schemaPath);
    const result = validateStandardSchema(schemaPath, validationScheme);
    console.log('Validation result:', result);
    return result;
  })

  constructor() {
    super();
    effect(() => {
      const user = this.authService.user();
      if (!user) {
        return;
      }
      const weightValue = String(user.weight ?? '').replace(/\D/g, '');
      const weight = this.weightOptions().find((f: any) => f.value === weightValue);
      this.formModel.set({
        first_name: user.first_name ?? '',
        last_name: user.last_name ?? '',
        sex: user.sex ?? '',
        birth_year: String(user.birth_year ?? ''),
        profile_for: String(user.profile_for?.id ?? ''),
        body_build: String(user.body_build?.id ?? ''),
        height: user.height ?? NaN,
        weight: weight ? weight.value : '',
        complexion: String(user.complexion?.id ?? ''),
      });
    })


  }

  generateRangeOptions(
    start: number = 35,
    end: number = 150,
    increment: number = 5
  ): Array<{ label: string; value: string }> {
    const options: Array<{ label: string; value: string }> = [];

    for (let i = start; i <= end; i += increment) {
      options.push({
        label: i.toString() + ' Kgs / ' + Math.round((i * 2.20)).toString() + 'LBS',
        value: i.toString()
      });
    }

    return options;
  }

  submit(e: any) {
    e.preventDefault();
    this.loading.set(true);
    this.http.post('user/update-profile', this.formModel())
      .pipe(
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe(() => {
        this.saved.emit();
        if (this.standalone()) {
          this.messageService.add({
            severity: 'custom',
            key: 'headless',
            summary: 'Profile updated successfully',
          });
        }
      })
  }
}
